// src/pages/OpenAccessWeek2025.tsx
import React from "react";
import { Link } from "react-router-dom";
import OAWeekHero from "../../components/OAWeekHero";
import PillLink from "../../components/PillLink";

type EventItem = {
  date: string;
  activity: React.ReactNode;
  theme: string;
  href?: string;
  localeNote?: string;
};

const EVENTS: EventItem[] = [
  {
    date: "Oct 20",
    activity: (
      <>
      <a href="https://indico.wacren.net/event/254/" target="_blank" rel="noreferrer" className="underline">LIBSENSE ECR Nigeria OA Week Webinar</a>
      </>
    ),
    theme:
      "Owning Our Research Infrastructure: Rethinking Visibility and the Hidden Costs of Access",
    href: "https://wacren.zoom.us/webinar/register/WN_bn2tfApiQVq9g8LWjuxUwA",
  },
  {
    date: "Oct 22",
    activity: (
      <>
        <a href="https://indico.wacren.net/event/250/" target="_blank" rel="noopener noreferrer" className="underline">Webinar
        <span className="block text-xs italic text-muted-foreground">Anglophone</span>
        </a> 
      </>
    ),
    theme:
      "Strengthening African scholarly journals through the Directory of Open Access Journals (DOAJ)",
    href: "https://wacren.zoom.us/webinar/register/WN_HJyH8rrgSz2LwF4giNDS3Q#/registration",
  },
  {
    date: "Oct 24",
    activity: (
      <>
      <a href="https://indico.wacren.net/event/253/" target="_blank" rel="noreferrer" className="underline">
       LIBSENSE ECR Ghana Virtual Roundtable
       </a>
      </>
    ),
    theme: "Creative Commons Licensing for Librarians and Researchers",
    href: "https://wacren.zoom.us/webinar/register/WN_XuXgovW3SIugnxA3BfTxjg#/registration",
  },
  {
    date: "Oct 30",
    activity: (
      <>
      <a href="https://indico.wacren.net/event/251/" target="_blank" rel="noopener noreferrer" className="underline">
        LIBSENSE–DOAJ Webinar
        <span className="block text-xs italic text-muted-foreground">Francophone</span>
        </a>
      </>
    ),
    theme:
      "Strengthening African scholarly journals through the Directory of Open Access Journals (DOAJ)",
    href: "https://wacren.zoom.us/webinar/register/WN_GpAWdM9jTPKCEOmRN6202A#/registration",
  },
];



const CtaButton: React.FC<React.PropsWithChildren<{ to: string }>> = ({ to, children }) => (
  <Link
    to={to}
    target="_blank"
    className="rounded-md bg-primary px-6 py-2 text-sm font-semibold text-white shadow hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/50"
  >
    {children}
  </Link>
);

const OpenAccessWeek2025: React.FC = () => {
  return (
    <main className="bg-background text-foreground mt-10">
      {/* Hero */}
       <OAWeekHero />

      {/* What is OA Week */}
      <section className="mx-auto max-w-6xl px-4 py-10">
<div className="flex items-start gap-3">
  {/* Orange OA lock */}
  <img
    src="/assets/images/logos/pngwing.com.png"
    alt="Open Access"
    className="h-12 w-auto"  // ~44px like the reference
  />

  {/* Wordmark (two lines) */}
  <div className="leading-none">
    {/* Line 1 */}
    <span className="block text-base md:text-lg font-semibold text-slate-600">
      International
    </span>

    {/* Line 2 */}
    <span className="block text-lg md:text-xl font-extrabold tracking-tight">
      <span className="text-slate-900">Open Access</span>
      <span className="text-orange-500 font-bold">&nbsp;Week</span>
    </span>
  </div>
</div>



        <h2 className="mt-6 text-xl font-semibold">What is International Open Access Week</h2>
        <p className="mt-3 text-sm leading-6 text-foreground">
          Open Access Week is a global event that promotes open access to knowledge — the free,
          immediate, and online availability of research outputs without financial, legal, or
          technical barriers.
        </p>
        <p className="mt-3 text-sm leading-6 text-foreground">
          The 2025 theme, <span className="font-medium">“Who Owns Our Knowledge?”</span>, invites
          reflection on issues of knowledge equity, control, and the power dynamics shaping
          scholarly communication. It challenges us to consider how knowledge can remain a shared
          public good, owned and governed by communities rather than corporations.
        </p>

        {/* Banner/graphic placeholder */}
        <div className="mt-10 flex h-full w-full rounded-md items-center justify-center">
          <img src="/assets/images/backgrounds/stage.png" alt="celebrate open access" />
        </div>
      </section>

      {/* LIBSENSE & OA in Africa */}
      <section className="mx-auto max-w-6xl px-4 py-10">
        <h2 className="text-xxl font-semibold md:text-xxxl">
          LIBSENSE and Open Access in Africa
        </h2>
        <p className="mt-4 text-sm leading-6">
          LIBSENSE has been a leading force in advancing open access across Africa. By bringing
          together key stakeholders in the scholarly communication ecosystem, we have fostered
          collective action toward equitable and sustainable models of open publishing — notably
          Diamond (no-fee) Open Access.
        </p>
        <p className="mt-3 text-sm leading-6">
          Our work spans policy advocacy, infrastructure development, and capacity building,
          ensuring that African research is not only visible and accessible but also globally
          connected. Through close collaborations with international partners such as COAR, EIFL, DOAJ, AJOL, and CAMES,
          LIBSENSE continues to shape the open-access landscape in Africa and beyond.
        </p>
        <p className="mt-3 text-sm leading-6">
          With sustained support, LIBSENSE is committed to deepening this impact — empowering
          communities, strengthening institutions, and advancing knowledge equity across the
          continent.
        </p>

    <section className="mx-auto max-w-6xl px-4 py-8">

<section className="mb-6 mx-auto max-w-6xl px-4 py-10">
  <div className="grid items-stretch gap-10 md:grid-cols-[1.05fr_1fr]">
    {/* Left column */}
    <div>
      <h3 className="text-lg font-semibold text-foreground">
        Our Open Access efforts include
      </h3>

      {/* Force vertical stacking + fixed readable width */}
      <ul className="mt-6 flex flex-col gap-3">
        <li className="md:mb-6">
          <PillLink to="/what-we-do/open-science-policy" >
            Supporting national open science policy frameworks
          </PillLink>
        </li>
        <li className="md:mb-6">
          <PillLink to="/about-us/project-initiatives">
            Promoting Diamond Open Access publishing in Africa
          </PillLink>
        </li>
        <li className="md:mb-6">
          <PillLink to="/what-we-do/open-infrastructure">
            Strengthening institutional repositories and visibility of African research
          </PillLink>
        </li>
        <li>
          <PillLink to="/what-we-do/capacity-building">
            Training and supporting librarians, early career researchers, and policymakers
          </PillLink>
        </li>
      </ul>
    </div>

    {/* Right block */}
       <div className="h-full md:h-[600px] w-full rounded">
      <img
        src="/assets/images/logos/logos-collection.jpg"
        alt="celebrate open access"
        className="h-full w-full object-contain rounded"
      />
    </div>
  </div>
</section>


</section>

      </section>

  {/* Activities Table */}
{/* Activities Table */}
<section className="w-full bg-primary py-12">
  <div className="mx-auto max-w-6xl px-4">
    <h2 className="text-center text-xxl font-bold text-white">
      OA Week 2025 Activities
    </h2>

    {/* Table header (desktop only) */}
    <div className="mt-6 hidden rounded-md bg-white px-5 py-3 md:block">
      <div className="grid grid-cols-12 text-[13px] font-semibold uppercase tracking-wide text-slate-900">
        <div className="col-span-2">Date</div>
        <div className="col-span-4">Activity</div>
        <div className="col-span-5">Theme</div>
        <div className="col-span-1 text-right">Link</div>
      </div>
    </div>

    {/* Rows */}
    <ul className="mt-3 space-y-3">
      {EVENTS.map((e, i) => (
        <li
          key={i}
          className="rounded-md bg-white p-5 text-[15px] text-slate-800 shadow-sm"
        >
          {/* Mobile/card layout by default; switches to grid on md+ */}
          <div className="md:grid md:grid-cols-12 md:items-start md:gap-3">
            {/* Date */}
            <div className="md:col-span-2">
              <span className="md:hidden block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Date
              </span>
              <span className="mt-1 inline-block rounded bg-primary px-2 py-1 text-sm text-white md:mt-0">
                {e.date}
              </span>
            </div>

            {/* Activity */}
            <div className="mt-3 md:col-span-4 md:mt-0">
              <span className="md:hidden block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Activity
              </span>
              <div className="mt-1 font-semibold text-foreground md:mt-0">
                {e.activity}
              </div>
            </div>

            {/* Theme */}
            <div className="mt-3 break-words leading-snug md:col-span-5 md:mt-0">
              <span className="md:hidden block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Theme
              </span>
              <div className="mt-1 md:mt-0 text-foreground">{e.theme}</div>
            </div>

            {/* Link */}
            <div className="mt-3 md:col-span-1 md:mt-0 md:text-right">
              <span className="md:hidden block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Link
              </span>
              {e.href ? (
                <Link to={e.href} className="mt-1 inline-block text-slate-900 underline md:mt-0">
                  Register
                </Link>
              ) : (
                <span className="mt-1 inline-block text-slate-400 md:mt-0">—</span>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  </div>
</section>


      {/* Get Involved */}
      <section className="mx-auto max-w-6xl px-4 py-14">
        <h2 className="text-center text-xxl font-bold text-red-500">Get Involved</h2>
        <p className="mt-4 text-center text-sm text-muted-foreground">
          Join the LIBSENSE Early Career Researcher (ECR) Team
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <CtaButton to="https://survey.wacren.net/index.php/184311?lang=en">Anglophone</CtaButton>
          <CtaButton to="https://survey.wacren.net/index.php/255586?lang=fr">Francophone</CtaButton>
          <CtaButton to="https://survey.wacren.net/index.php/291677?lang=pt">Portuguese</CtaButton>
        </div>
      </section>
    </main>
  );
};

export default OpenAccessWeek2025;
